/**
 * BlogListPage - Main blog listing page showing all posts
 */

import { Link } from "react-router-dom";
import { usePageTitle } from "../../hooks";
import { getAllPosts, CATEGORIES } from "../../lib/blog";
import { SEOHead, BlogCard } from "../../components/blog";

export function BlogListPage() {
  usePageTitle("Blog - Early Learning Tips for Parents");
  const posts = getAllPosts();

  return (
    <div className="flex flex-col">
      <SEOHead
        title="JumpBean Blog"
        description="Expert advice on coding for preschoolers, educational screen time, kindergarten readiness, and play-based learning from early childhood educators."
        canonical="/blog"
        type="website"
      />

      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-gradient-to-b from-magic-100 via-sunshine-50 to-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="font-display font-extrabold text-display-lg md:text-display-xl text-neutral-900 mb-6">
            JumpBean Blog
          </h1>
          <p className="font-body text-body-lg md:text-body-xl text-neutral-600 max-w-2xl mx-auto">
            Expert tips for parents who want their preschoolers to{" "}
            <span className="text-magic-600 font-semibold">love learning</span>
          </p>
        </div>
      </section>

      {/* Category Pills */}
      <section className="py-6 bg-white border-b border-neutral-100">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex flex-wrap gap-3 justify-center">
            {CATEGORIES.map((category) => (
              <Link
                key={category.slug}
                to={`/blog/${category.slug}`}
                className="inline-flex items-center px-5 py-2.5 rounded-full font-body text-body-md font-semibold
                         bg-neutral-100 text-neutral-700
                         hover:bg-magic-100 hover:text-magic-700
                         transition-colors leading-none"
              >
                {category.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          {posts.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => (
                <BlogCard key={`${post.category}/${post.slug}`} post={post} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="font-body text-body-lg text-neutral-500">
                Blog posts coming soon!
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-16 bg-gradient-to-b from-sunshine-50 to-white">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <h2 className="font-display font-bold text-display-sm text-neutral-900 mb-4">
            Get learning tips in your inbox
          </h2>
          <p className="font-body text-body-md text-neutral-600 mb-6">
            Join parents who receive weekly tips on early coding, screen time,
            and kindergarten readiness.
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full
                     font-display font-bold text-neutral-900
                     bg-sunshine-400 hover:bg-sunshine-500
                     transition-colors shadow-playful hover:shadow-playful-lg"
          >
            Start Free Trial
            <span className="text-lg">→</span>
          </Link>
        </div>
      </section>
    </div>
  );
}

export default BlogListPage;
